/**
 * @fileoverview Defines the `Ignorer` interface and implementations like `FileTypeIgnorer`, `NonTextFileIgnorer`, `IgnoreFileIgnorer`, and `DirectoryIgnorer` for filtering files. Includes functions `getParentGitIgnorers` and `getIgnorersForDir` to retrieve relevant ignorers. The `DirectoryIgnorer` class composes multiple `Ignorer` instances for comprehensive filtering based on directory context.
 */

import * as fs from 'fs'
import * as pathlib from 'path'
import ignore from 'ignore'
import textExtensions from 'textextensions'
import { getRepoRoot, isInGitRepo } from './git'
import { readSparsePath, SparsePath } from './sparse_path'

export interface Ignorer {
  shouldSkip(path: string): boolean
}

export class FileTypeIgnorer implements Ignorer {
  IGNORE_FILES = ['node_modules', 'package-lock.json', '.git', '.firebase']
  IMAGE_SUFFIXES = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp']
  MUSIC_VIDEO_SUFFIXES = ['.mp3', '.wav', '.mp4', '.avi', '.mov']
  ML_SUFFIXES = ['.parquet', '.pt']

  shouldSkip(path: string): boolean {
    const fileName = pathlib.basename(path)
    return (
      this.ML_SUFFIXES.some((suffix) => fileName.endsWith(suffix)) ||
      this.IGNORE_FILES.includes(fileName) ||
      this.IMAGE_SUFFIXES.some((suffix) => fileName.endsWith(suffix)) ||
      this.MUSIC_VIDEO_SUFFIXES.some((suffix) => fileName.endsWith(suffix)) ||
      fileName.includes('.DS_Store')
    )
  }
}

export class NonTextFileIgnorer implements Ignorer {
  TEXT_EXTENSIONS = new Set(textExtensions)

  // Function to validate if a buffer contains valid UTF-8 byte sequences (credit: ChatGPT. seems to work spot checking.)
  isValidUTF8(buf: Buffer): boolean {
    let i = 0
    while (i < buf.length) {
      if (buf[i]! <= 0x7f) {
        // ASCII (0x00-0x7F)
        i++
      } else if ((buf[i]! & 0xe0) === 0xc0) {
        // 2-byte sequence (0xC0-0xDF)
        if (i + 1 >= buf.length || (buf[i + 1]! & 0xc0) !== 0x80) return false
        i += 2
      } else if ((buf[i]! & 0xf0) === 0xe0) {
        // 3-byte sequence (0xE0-0xEF)
        if (i + 2 >= buf.length || (buf[i + 1]! & 0xc0) !== 0x80 || (buf[i + 2]! & 0xc0) !== 0x80)
          return false
        i += 3
      } else if ((buf[i]! & 0xf8) === 0xf0) {
        // 4-byte sequence (0xF0-0xF7)
        if (
          i + 3 >= buf.length ||
          (buf[i + 1]! & 0xc0) !== 0x80 ||
          (buf[i + 2]! & 0xc0) !== 0x80 ||
          (buf[i + 3]! & 0xc0) !== 0x80
        )
          return false
        i += 4
      } else {
        return false // Invalid byte sequence
      }
    }
    return true // Valid UTF-8
  }

  isTextFile(filePath: string): boolean {
    // First check if the file has a text extension and if so, assume it's a text file.
    const isTextExtension = this.TEXT_EXTENSIONS.has(pathlib.extname(filePath).replace('.', ''))
    if (isTextExtension) {
      return true
    }

    // If the file extension is not in our approved list, check the first 512 bytes for valid UTF-8.
    const buffer = Buffer.alloc(512)
    const fd = fs.openSync(filePath, 'r')

    try {
      const bytesRead = fs.readSync(fd, buffer, 0, 512, 0)
      return this.isValidUTF8(buffer.subarray(0, bytesRead))
    } finally {
      fs.closeSync(fd)
    }
  }

  shouldSkip(path: string): boolean {
    if (fs.existsSync(path) && fs.lstatSync(path).isFile()) {
      return !this.isTextFile(path)
    }
    return false
  }
}

export class SymlinkIgnorer implements Ignorer {
  shouldSkip(path: string): boolean {
    return fs.existsSync(path) && fs.lstatSync(path).isSymbolicLink()
  }
}

export class IgnoreFileIgnorer implements Ignorer {
  private directory: string
  private patterns: string[] = []
  private ignorePath: string
  private ig = ignore()

  constructor(directory: string, ignoreFileName: string) {
    this.directory = directory
    this.ignorePath = pathlib.join(directory, ignoreFileName)
    this.loadPatterns()
  }

  private loadPatterns() {
    if (fs.existsSync(this.ignorePath)) {
      const content = fs.readFileSync(this.ignorePath, 'utf-8')
      this.patterns = content
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line && !line.startsWith('#'))
    }
    this.ig.add(this.patterns)
  }

  shouldSkip(path: string): boolean {
    const pathToCheck = pathlib.resolve(path)
    const relativePath = pathlib.relative(this.directory, pathToCheck)
    return relativePath.length > 0 && this.ig.ignores(relativePath)
  }
}

/**
 * Returns a list of ignorers for all parent gitignore files.
 *
 * This is used because a user may choose to reduce context by picking up subdir but
 * not realize a parent dir gitignore is reducing context event more.
 */
export function getParentGitIgnorers(cwd: string): Ignorer[] {
  if (!isInGitRepo(cwd)) {
    return []
  }

  const repoRoot = getRepoRoot(cwd)!
  if (cwd === repoRoot) {
    return []
  }

  const cwdPath = pathlib.resolve(cwd)
  const repoRootPath = pathlib.resolve(repoRoot)
  const relativePath = pathlib.relative(repoRootPath, cwdPath)

  const directories: string[] = [repoRootPath]
  const parts = relativePath.split(pathlib.sep)
  for (let i = 0; i < parts.length - 1; i++) {
    const lastPath: string = directories[directories.length - 1]!
    const nextPath = pathlib.join(lastPath, parts[i]!)
    directories.push(nextPath)
  }

  return directories.map((dir) => new IgnoreFileIgnorer(dir, '.gitignore'))
}

export const DEFAULT_IGNORERS: Ignorer[] = [
  new NonTextFileIgnorer(),
  new FileTypeIgnorer(),
  new SymlinkIgnorer(),
]

/**
 * Compose all of the Ignorers implemented above into a single Ignorer to be
 * constructed for a directory.
 */
export class DirectoryIgnorer implements Ignorer {
  private baseIgnorers: Ignorer[]
  private directoryToIgnorers: Map<string, Ignorer[]>
  private directory: string
  private allowedSubPaths?: string[]

  constructor(sparseDir: SparsePath, additionalIgnorers: Ignorer[] = []) {
    const { path, allowedSubPaths } = readSparsePath(sparseDir)
    this.directory = path
    this.allowedSubPaths = allowedSubPaths
    this.baseIgnorers = [
      ...DEFAULT_IGNORERS,
      ...additionalIgnorers,
      ...getParentGitIgnorers(this.directory),
    ]
    this.directoryToIgnorers = new Map<string, Ignorer[]>()
  }

  shouldSkip(path: string): boolean {
    if (this.allowedSubPaths && this.allowedSubPaths.length > 0) {
      const relativePath = pathlib.relative(this.directory, path)
      if (!this.allowedSubPaths.some((allowed) => relativePath.startsWith(allowed))) {
        return true
      }
    }

    let skip = this.baseIgnorers.some((ignorer) => ignorer.shouldSkip(path))
    if (skip) {
      return skip
    }

    return this.ignorers(path).some((ignorer) => ignorer.shouldSkip(path))
  }

  // Recursively get all ignorers for a path.
  ignorers(path: string): Ignorer[] {
    if (this.directoryToIgnorers.has(path)) {
      return this.directoryToIgnorers.get(path)!
    }

    const relevantIgnorers: Ignorer[] = []

    if (fs.lstatSync(path).isDirectory()) {
      if (isInGitRepo(path)) {
        relevantIgnorers.push(new IgnoreFileIgnorer(path, '.gitignore'))
      }
    }
    const parentPath = pathlib.dirname(path)
    if (parentPath.length >= this.directory.length) {
      relevantIgnorers.push(...this.ignorers(parentPath))
    }

    if (!this.directoryToIgnorers.has(path)) {
      this.directoryToIgnorers.set(path, relevantIgnorers)
    }

    return relevantIgnorers
  }
}
